// Share helpers for the Bank Registry card — text-only (no details page to
// link to, unlike Horti's provider share on rp-test-sharable). Two layers:
//   1. `nativeShare` — the Web Share API, the platform's own share sheet.
//      Present on Android/iOS/Windows/macOS Chrome & Safari.
//   2. `ShareBankDetailsDialog` — the in-app fallback for platforms without
//      the native sheet (notably Linux desktop Chrome, where
//      `navigator.share` is `undefined` even on a secure origin).

/** True when this browser exposes the native Web Share API. */
export const canNativeShare = () =>
  typeof navigator !== "undefined" && typeof navigator.share === "function";

/**
 * Open the platform's native share sheet. Resolves to:
 *   "shared"      — the native sheet completed (it shows its own confirmation).
 *   "cancelled"   — the user dismissed the sheet (not an error).
 *   "unavailable" — this browser has no Web Share API (use the dialog instead).
 *   "failed"      — the API exists but rejected for some other reason.
 */
export const nativeShare = async ({ title, text }) => {
  if (!text) return "failed";
  if (!canNativeShare()) return "unavailable";
  try {
    await navigator.share({ title, text });
    return "shared";
  } catch (err) {
    // Dismissing the sheet rejects with AbortError — treat as a no-op.
    if (err?.name === "AbortError") return "cancelled";
    return "failed";
  }
};

/** Copy `text` to the clipboard. Resolves true on success, false otherwise. */
export const copyText = async (text) => {
  if (typeof navigator === "undefined" || !navigator.clipboard?.writeText) return false;
  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch {
    return false;
  }
};

/**
 * Deep links for the in-app share dialog's targets. Each opens the target
 * app's own compose screen pre-filled — it never sends on the user's behalf
 * (the user still taps send), exactly like the native sheet.
 */
export const shareTargetUrls = ({ text, subject }) => ({
  whatsapp: `https://wa.me/?text=${encodeURIComponent(text)}`,
  email: `mailto:?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(text)}`,
});
